import React, { useState, useEffect, useContext } from "react";
import {
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Container,
  IconButton,
} from "@mui/material";
import {
  Search as SearchIcon,
  Download as DownloadIcon,
  ArrowBack as BackIcon,
  ArrowForward as NextIcon,
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import CustomTable from "../../../../custom-components/CustomTable";
import CagLocatorsTable from "../../../../output-components/cag-locators/CagLocatorTable";
import CustomStepper from "../../../../custom-components/CustomStepper";
import {
  getStepperSteps,
  getStepIndex,
  getNextStep,
} from "../../../../../utils/stepperConfig";
import { playwrightTypeScriptPageObjectsAPI } from "../../../../../services/webServices";
import { LoadingModalContext } from "../../../../main-pages/ServicePage";
import MessagePopup from "../../../../popup-components/MessagePopup";

/**
 * GenerateLocatorsPage - Displays generated locators in a table format
 *
 * Data Flow:
 * - Receives generatedLocators from previous step (IntroduceSUTPage)
 * - Uses test case data to display relevant locators
 * - Falls back to test case-based sample data if no generated locators
 * - Supports search, edit, and download functionality
 * - Maintains data format for download and edit operations
 */

const GenerateLocatorsPage = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [locatorsData, setLocatorsData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [editingRowId, setEditingRowId] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const { setLoadingState } = useContext(LoadingModalContext);
  const location = useLocation();
  const navigate = useNavigate();

  // Get data from previous step
  const {
    generatedLocators,
    pageSource,
    testCases,
    currentTestCaseIndex = 0,
    totalTestCases = 35,
    automationType,
    preferences,
    environment,
    currentStep,
    currentStepIndex: stateStepIndex,
  } = location.state || {};

  // Get current test case data
  const currentTestCase = testCases?.[currentTestCaseIndex] || {
    testCaseId: "TCD_FT_07_NEG",
    title: "Invalid Country Code",
    testSteps:
      "Attempt to retrieve weather data using a valid city name but an invalid country code.",
    expectedResult: "Regular text column",
  };

  // No more hardcoded sample data - we'll show proper no data message instead

  // Function to transform API response to table data
  const transformApiResponseToTableData = (apiResponse) => {
    console.log("🔍 Transforming API response:", apiResponse);
    console.log("🔍 API response type:", typeof apiResponse);
    console.log(
      "🔍 API response keys:",
      apiResponse ? Object.keys(apiResponse) : "N/A"
    );

    if (!apiResponse || typeof apiResponse !== "object") {
      console.log("❌ Invalid API response, returning no data");
      return [
        {
          id: 1,
          parameters: "No Data",
          primaryLocators: "Invalid API response",
          secondaryLocators: "Please try again",
          originalData: {
            fieldName: "No Data",
            primary: [],
            secondary: [],
          },
        },
      ];
    }

    return Object.entries(apiResponse).map(([fieldName, locators], index) => {
      console.log(`🔍 Processing field: ${fieldName}`, locators);

      // Handle new format: { primary: "string", secondary: "string" } or { primary: [...], secondary: [...] }
      if (locators && (locators.primary || locators.secondary)) {
        // Handle both string and array formats
        const primary = Array.isArray(locators.primary)
          ? locators.primary
          : typeof locators.primary === "string"
            ? [locators.primary]
            : [];
        const secondary = Array.isArray(locators.secondary)
          ? locators.secondary
          : typeof locators.secondary === "string"
            ? [locators.secondary]
            : [];

        return {
          id: index + 1,
          parameters: fieldName,
          primaryLocators: primary.join(", "),
          secondaryLocators: secondary.join(", "),
          // Store original data for edit functionality
          originalData: {
            fieldName,
            primary: primary,
            secondary: secondary,
          },
        };
      }

      // Handle simple string format (legacy backend format): "getByLabel('Username')"
      if (typeof locators === "string" && locators.trim()) {
        return {
          id: index + 1,
          parameters: fieldName,
          primaryLocators: locators,
          secondaryLocators: "",
          // Store original data for edit functionality
          originalData: {
            fieldName,
            primary: [locators],
            secondary: [],
          },
        };
      }

      // Handle old complex format: { getByRole: "...", getByTestId: "...", cssSelector: "...", xpath: "..." }
      if (
        locators &&
        typeof locators === "object" &&
        !Array.isArray(locators)
      ) {
        // Convert to arrays for display
        const primaryArray = [];
        const secondaryArray = [];

        // Add each locator type to appropriate array
        if (locators.getByRole) primaryArray.push(locators.getByRole);
        if (locators.getByTestId) primaryArray.push(locators.getByTestId);
        if (locators.cssSelector) secondaryArray.push(locators.cssSelector);
        if (locators.xpath) secondaryArray.push(locators.xpath);

        return {
          id: index + 1,
          parameters: fieldName,
          primaryLocators: primaryArray.join(", "),
          secondaryLocators: secondaryArray.join(", "),
          // Store original data for edit functionality
          originalData: {
            fieldName,
            primary: primaryArray,
            secondary: secondaryArray,
          },
        };
      }

      // Fallback for unexpected format
      console.log(`⚠️ Unexpected locator format for ${fieldName}:`, locators);
      return {
        id: index + 1,
        parameters: fieldName,
        primaryLocators: "No locators found",
        secondaryLocators: "No locators found",
        originalData: {
          fieldName,
          primary: [],
          secondary: [],
        },
      };
    });
  };

  // Initialize with data from previous step or show no data message
  useEffect(() => {
    if (
      generatedLocators &&
      typeof generatedLocators === "object" &&
      Object.keys(generatedLocators).length > 0
    ) {
      // Use the generated locators from the previous step
      console.log("✅ Using generated locators from API:", generatedLocators);
      const transformedData =
        transformApiResponseToTableData(generatedLocators);
      setLocatorsData(transformedData);
    } else {
      // Show no data message instead of hardcoded sample data
      console.log("❌ No valid generated locators, showing no data message");
      setLocatorsData([
        {
          id: 1,
          parameters: "No Data",
          primaryLocators: "No locators generated",
          secondaryLocators: "Please go back and generate locators first",
          originalData: {
            fieldName: "No Data",
            primary: [],
            secondary: [],
          },
        },
      ]);
    }
  }, [generatedLocators, currentTestCase]);

  // Function to add new locator (for future use)
  const addLocator = (fieldName, primary, secondary) => {
    const newLocator = {
      id: locatorsData.length + 1,
      parameters: fieldName,
      primaryLocators: Array.isArray(primary) ? primary.join(", ") : primary,
      secondaryLocators: Array.isArray(secondary)
        ? secondary.join(", ")
        : secondary,
      originalData: {
        fieldName,
        primary: Array.isArray(primary) ? primary : [primary],
        secondary: Array.isArray(secondary) ? secondary : [secondary],
      },
    };
    setLocatorsData((prev) => [...prev, newLocator]);
  };

  // Function to update locator (for edit functionality)
  const updateLocator = (id, updatedData) => {
    setLocatorsData((prev) =>
      prev.map((row) =>
        row.id === id
          ? {
              ...row,
              parameters: updatedData.fieldName,
              primaryLocators: Array.isArray(updatedData.primary)
                ? updatedData.primary.join(", ")
                : updatedData.primary,
              secondaryLocators: Array.isArray(updatedData.secondary)
                ? updatedData.secondary.join(", ")
                : updatedData.secondary,
              originalData: {
                fieldName: updatedData.fieldName,
                primary: Array.isArray(updatedData.primary)
                  ? updatedData.primary
                  : [updatedData.primary],
                secondary: Array.isArray(updatedData.secondary)
                  ? updatedData.secondary
                  : [updatedData.secondary],
              },
            }
          : row
      )
    );
  };

  // Function to delete locator
  const deleteLocator = (id) => {
    setLocatorsData((prev) => prev.filter((row) => row.id !== id));
  };

  const columns = [
    {
      key: "parameters",
      label: "Parameters",
      sortable: true,
      maxWidth: "200px",
      render: (row) => {
        if (editingRowId === row.id) {
          return (
            <TextField
              size="small"
              value={editFormData.parameters || row.parameters}
              onChange={(e) => setEditFormData(prev => ({ ...prev, parameters: e.target.value }))}
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontSize: "14px",
                  fontFamily: "Manrope",
                },
              }}
            />
          );
        }
        return row.parameters;
      },
    },
    {
      key: "primaryLocators",
      label: "Primary Locators",
      sortable: true,
      maxWidth: "200px",
      render: (row) => {
        if (editingRowId === row.id) {
          return (
            <TextField
              size="small"
              value={editFormData.primaryLocators || row.primaryLocators}
              onChange={(e) => setEditFormData(prev => ({ ...prev, primaryLocators: e.target.value }))}
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontSize: "14px",
                  fontFamily: "Manrope",
                },
              }}
            />
          );
        }
        return row.primaryLocators;
      },
    },
    {
      key: "secondaryLocators",
      label: "Secondary Locators",
      sortable: true,
      maxWidth: "200px",
      render: (row) => {
        if (editingRowId === row.id) {
          return (
            <TextField
              size="small"
              value={editFormData.secondaryLocators || row.secondaryLocators}
              onChange={(e) => setEditFormData(prev => ({ ...prev, secondaryLocators: e.target.value }))}
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontSize: "14px",
                  fontFamily: "Manrope",
                },
              }}
            />
          );
        }
        return row.secondaryLocators;
      },
    },
  ];

  // Function to call the actual API (for future use)
  const fetchLocatorsFromAPI = async (pageSourceData) => {
    setIsLoading(true);
    try {
      // Replace with your actual API call
      // const response = await fetch('/api/generate-locators', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify({ pageSource: pageSourceData })
      // });
      // const apiResponse = await response.json();

      // Use the generated locators from the API response
      if (generatedLocators) {
        const transformedData =
          transformApiResponseToTableData(generatedLocators);
        setLocatorsData(transformedData);
      }
    } catch (error) {
      console.error("Error fetching locators:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to convert table data back to API format
  const convertTableDataToApiFormat = (tableData) => {
    console.log("🔍 Converting table data to API format:", tableData);

    return tableData.reduce((acc, row) => {
      // Convert back to the new backend format: { getByRole: "...", getByTestId: "...", cssSelector: "...", xpath: "..." }
      const locators = {};

      // Map primary locators to getByRole and getByTestId
      if (row.originalData.primary && row.originalData.primary.length > 0) {
        row.originalData.primary.forEach((locator, index) => {
          if (index === 0 && locator.includes("getByRole")) {
            locators.getByRole = locator;
          } else if (index === 1 && locator.includes("getByTestId")) {
            locators.getByTestId = locator;
          } else if (index === 0) {
            locators.getByRole = locator; // Default to getByRole if not specified
          }
        });
      }

      // Map secondary locators to cssSelector and xpath
      if (row.originalData.secondary && row.originalData.secondary.length > 0) {
        row.originalData.secondary.forEach((locator, index) => {
          if (locator.includes("cssSelector")) {
            locators.cssSelector = locator;
          } else if (locator.includes("xpath")) {
            locators.xpath = locator;
          } else if (index === 0) {
            locators.cssSelector = locator; // Default to cssSelector if not specified
          } else if (index === 1) {
            locators.xpath = locator; // Default to xpath if not specified
          }
        });
      }

      acc[row.parameters] = locators;
      return acc;
    }, {});
  };

  const handleEdit = (row) => {
    console.log("Edit locator:", row);
    setEditingRowId(row.id);
    setEditFormData({
      parameters: row.parameters,
      primaryLocators: row.primaryLocators,
      secondaryLocators: row.secondaryLocators,
    });
  };

  const handleSave = (rowId) => {
    console.log("Save locator:", rowId, editFormData);
    
    // Update the locator data
    const updatedData = {
      fieldName: editFormData.parameters,
      primary: editFormData.primaryLocators.split(",").map(s => s.trim()).filter(s => s),
      secondary: editFormData.secondaryLocators.split(",").map(s => s.trim()).filter(s => s),
    };
    
    updateLocator(rowId, updatedData);
    
    // Reset edit state
    setEditingRowId(null);
    setEditFormData({});
  };

  const normalizeLocators = (response) => {
  // If wrapped in { data: [ { output: ... } ] }
  if (response?.data?.[0]?.output) {
    return response;
  }

  // If response is array → convert to object
  if (Array.isArray(response)) {
    const output = {};
    response.forEach((item, index) => {
      const key = item.element_name || `element_${index + 1}`;
      output[key] = {
        element_name: item.element_name || key,
        inside_frame: item.inside_frame ?? false,
        frame_selector: item.frame_selector ?? null,
        inside_shadow_dom: item.inside_shadow_dom ?? false,
        shadow_host_selector: item.shadow_host_selector ?? null,
        primary: item.primary || [],
        secondary: item.secondary || [],
      };
    });
    return { data: [{ output }] };
  }

  // If plain object → wrap for table
  return { data: [{ output: response }] };
};


  const handleCancel = () => {
    console.log("Cancel edit");
    setEditingRowId(null);
    setEditFormData({});
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleDownload = () => {
    console.log("Download locators");
    // Convert table data back to API format
    const downloadData = convertTableDataToApiFormat(locatorsData);

    // Create and download JSON file
    const dataStr = JSON.stringify(downloadData, null, 2);
    const dataBlob = new Blob([dataStr], { type: "application/json" });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "locators.json";
    link.click();
    URL.revokeObjectURL(url);
  };

  // Get dynamic steps based on framework selection
  const progressSteps = getStepperSteps(preferences);
  const currentStepIndex =
    stateStepIndex !== undefined
      ? stateStepIndex
      : getStepIndex(progressSteps, "Generate Locators");

  // Helper function to get route path based on step name
  const getStepPath = (stepName) => {
    const stepPaths = {
      "Test Case Imported":
        "/dashboard/test-automation/playwright-typescript-mocha/ntnp/test-case-imported",
      "Introduce SUT":
        "/dashboard/test-automation/playwright-typescript-mocha/ntnp/introduce-sut",
      "Generate Locators":
        "/dashboard/test-automation/playwright-typescript-mocha/ntnp/generate-locators",
      "Generate Page Objects":
        "/dashboard/test-automation/playwright-typescript-mocha/ntnp/generate-page-objects",
      "Generate Test Files":
        "/dashboard/test-automation/playwright-typescript-mocha/ntnp/generate-test-files",
    };
    return (
      stepPaths[stepName] ||
      "/dashboard/test-automation/playwright-typescript-mocha/ntnp/generate-locators"
    );
  };

  const handleBack = () => {
    navigate("/dashboard/test-automation/playwright-typescript-mocha/ntnp/introduce-sut", {
      state: {
        automationType,
        preferences,
        environment,
        testCases,
        currentTestCaseIndex,
        totalTestCases,
        pageSource,
      },
    });
  };

  const handleNext = async () => {
    let fake;
    const startProgress = (setProgress) => {
      let progress = 0;
      let stop = false;
      let finalMessageShown = false;

      const interval = setInterval(() => {
        if (stop) return;

        progress += 2.4;
        if (progress >= 98 && !finalMessageShown) {
          finalMessageShown = true;
          setLoadingState((prev) => ({
            ...prev,
            message: "Almost done! Fetching your output",
          }));
        }

        setLoadingState((prev) => ({
          ...prev,
          progress: Math.min(progress, 98),
        }));
      }, 100);

      return {
        completeTo100: async () => {
          stop = true;
          clearInterval(interval);
          setLoadingState((prev) => ({
            ...prev,
            progress: 100,
          }));
          await new Promise((resolve) => setTimeout(resolve, 500));
        },
      };
    };

    const finishTo100AndClose = async () => {
      await fake.completeTo100();
      setLoadingState({ open: false, message: "", progress: null });
    };

    // Initial message
    setLoadingState({
      open: true,
      message: "Generating Page Objects",
      progress: 0,
    });

    fake = startProgress(setLoadingState);

    try {
      // Prepare data for page objects generation
      const requestData = {
        locators: convertTableDataToApiFormat(locatorsData),
        testCases: testCases,
        currentTestCaseIndex: currentTestCaseIndex,
        pageSource: pageSource,
        automationType: automationType,
        preferences: preferences,
        environment: environment,
      };

      // Call the Playwright TypeScript Page Objects API
      const response = await playwrightTypeScriptPageObjectsAPI(requestData);
      const generatedPageObjects = response.data;

      await finishTo100AndClose();

      const nextStep = getNextStep(progressSteps, currentStepIndex);
      if (nextStep) {
        const nextStepPath = getStepPath(nextStep);
        navigate(nextStepPath, {
          state: {
            automationType,
            preferences,
            environment,
            testCases,
            currentTestCaseIndex,
            totalTestCases,
            currentStep: nextStep,
            currentStepIndex: currentStepIndex + 1,
            generatedLocators,
            generatedPageObjects,
            pageSource,
          },
        });
      }
    } catch (error) {
      console.error("Error generating page objects:", error);
      const errorMsg =
        error?.response?.data?.Error ||
        error?.response?.data?.message ||
        "An error occurred. Please try again.";
      setPopupMessage(errorMsg);
      setPopupOpen(true);
      setLoadingState({ open: false, message: "", progress: null });

      // Still navigate to next page even if API fails
      const nextStep = getNextStep(progressSteps, currentStepIndex);
      if (nextStep) {
        const nextStepPath = getStepPath(nextStep);
        navigate(nextStepPath, {
          state: {
            automationType,
            preferences,
            environment,
            testCases,
            currentTestCaseIndex,
            totalTestCases,
            currentStep: nextStep,
            currentStepIndex: currentStepIndex + 1,
            generatedLocators,
            pageSource,
          },
        });
      }
    }
  };

  // Filter data based on search term
  const filteredData = locatorsData.filter(
    (row) =>
      row.parameters.toLowerCase().includes(searchTerm.toLowerCase()) ||
      row.primaryLocators.toLowerCase().includes(searchTerm.toLowerCase()) ||
      row.secondaryLocators.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      <MessagePopup
        open={popupOpen}
        onClose={() => setPopupOpen(false)}
        message={popupMessage}
      />
      <Box
        sx={{
          minHeight: "100vh",
          background: "#F6F3F2",
          display: "flex",
          flexDirection: "column",
          py: 4,
        }}
      >
        <Container maxWidth="lg" sx={{ width: "100%", flex: 1 }}>
          {/* Custom Stepper */}
          <Box sx={{ mb: 4 }}>
            <CustomStepper
              steps={progressSteps}
              activeStep={currentStepIndex}
              maxWidth="100%"
              containerWidth="100%"
              activeColor="#1976d2"
              completedColor="#40a9ff"
              pendingColor="#d1d1d1"
              showNextStep={true}
              showStepCount={true}
              title="Now"
              inProgressText="(In Progress)"
              completedText="(Completed)"
              nextText="Next"
              completedNextText="Completed"
              stepText="Step"
            />
          </Box>

          {/* Page Title */}
          <Typography
            variant="h4"
            component="h1"
            sx={{
              mb: { xs: 2, sm: 3, md: 4 },
              fontWeight: "bold",
              color: "#333",
              fontSize: { xs: "20px", sm: "24px", md: "28px" },
              fontFamily: "Manrope",
              lineHeight: { xs: 1.3, sm: 1.4 },
            }}
          >
            Locators
          </Typography>

          {/* Main Content Card */}
          <Card
            sx={{
              backgroundColor: "white",
              borderRadius: { xs: "4px", sm: "8px" },
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              mb: { xs: 2, sm: 3, md: 4 },
              overflow: "hidden",
            }}
          >
            <CardContent sx={{ p: 0 }}>
              {/* Search Bar */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: { xs: 2, sm: 0 },
                  p: { xs: 2, sm: 3 },
                  borderBottom: "1px solid #e0e0e0",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: "Manrope",
                    fontWeight: 600,
                    color: "#333",
                    fontSize: { xs: "16px", sm: "18px" },
                    textAlign: { xs: "center", sm: "left" },
                    width: { xs: "100%", sm: "auto" },
                  }}
                >
                  Locators
                </Typography>

                <TextField
                  placeholder="Search"
                  value={searchTerm}
                  onChange={handleSearch}
                  size="small"
                  sx={{
                    width: { xs: "100%", sm: "300px" },
                    "& .MuiOutlinedInput-root": {
                      borderRadius: { xs: "4px", sm: "8px" },
                    },
                    "& .MuiInputLabel-root": {
                      fontFamily: "Manrope",
                    },
                    "& .MuiInputBase-input": {
                      fontFamily: "Manrope",
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: "#666" }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {/* Table */}
              {/* <CustomTable
                data={filteredData}
                columns={columns}
                searchable={false}
                selectable={false}
                sortable={true}
                isEdit={true}
                onEdit={handleEdit}
                editingRowId={editingRowId}
                onSave={handleSave}
                onCancel={handleCancel}
                onEditChange={(field, value) => setEditFormData(prev => ({ ...prev, [field]: value }))}
                editFormData={editFormData}
                showTitle={false}
                showSearch={false}
                containerProps={{
                  sx: { boxShadow: "none" },
                }}
              /> */}
               <CagLocatorsTable
                            jsonData={normalizeLocators(generatedLocators)}
                            setJsonData={setLocatorsData}
                          />
            </CardContent>
          </Card>

          {/* Footer Navigation */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              backgroundColor: "transparent",
              borderRadius: "0 0 8px 8px",
            }}
          >
            {/* Left: Back Button */}
            <Button
              variant="outlined"
              startIcon={<BackIcon />}
              onClick={handleBack}
              sx={{
                width: 120,
                height: 40,
                border: "2px solid #0094FB",
                borderRadius: 2,
                backgroundColor: "transparent",
                padding: "8px 16px",
                fontFamily: "Manrope",
                fontWeight: 700,
                fontSize: 20,
                lineHeight: "24px",
                letterSpacing: 0.15,
                color: "#0094FB",
                textTransform: "none",
                "&:hover": {
                  border: "2px solid #0094FB",
                  backgroundColor: "rgba(0, 148, 251, 0.04)",
                },
                transition: "all 0.3s ease-out",
              }}
            >
              Back
            </Button>

            {/* Right: Download and Generate Page Objects Buttons */}
            <Box sx={{ display: "flex", gap: 2 }}>
              {/* Download Button */}
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleDownload}
                sx={{
                  width: 252,
                  height: 40,
                  border: "2px solid #0094FB",
                  borderRadius: 2,
                  backgroundColor: "transparent",
                  padding: "8px 16px",
                  fontFamily: "Manrope",
                  fontWeight: 700,
                  fontSize: 20,
                  lineHeight: "24px",
                  letterSpacing: 0.15,
                  color: "#0094FB",
                  textTransform: "none",
                  "&:hover": {
                    border: "2px solid #0094FB",
                    backgroundColor: "rgba(0, 148, 251, 0.04)",
                  },
                  transition: "all 0.3s ease-out",
                }}
              >
                Download Locators
              </Button>

              {/* Next Button */}
              <Button
                variant="contained"
                endIcon={<NextIcon />}
                onClick={handleNext}
                sx={{
                  width: 284,
                  height: 40,
                  backgroundColor: "#0094FB",
                  borderRadius: 2,
                  padding: "8px 16px",
                  fontFamily: "Manrope",
                  fontWeight: 700,
                  fontSize: 20,
                  lineHeight: "24px",
                  letterSpacing: 0.15,
                  color: "white",
                  textTransform: "none",
                  border: "none",
                  "&:hover": {
                    backgroundColor: "#007ACC",
                  },
                  transition: "all 0.3s ease-out",
                }}
              >
                Generate Page Objects
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default GenerateLocatorsPage;
